import { Query, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { Socket, io } from "socket.io-client";

import {
  EncounterStartType,
  type EventModel,
  type FrontendSideEffectEvent,
} from "@/api/generated/models";
import { LayoutContext } from "@/components";
import { useAuthState } from "@/hooks/useAuthState";
import useGetAuthToken from "@/hooks/useAuthToken";
import {
  infiniteMessagesQueryKey,
  updateImageGenerationStatus,
  updateInfiniteMessagesFromSocket,
} from "@/hooks/useInfiniteMessages";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { captureException, logger } from "@/sentry";
import { useNudgeStore, useVideoStore } from "@/store";
import { useAgeVerificationStore } from "@/store/ageVerification";
import { useCSATSurveyStore } from "@/store/survey";
import { CSAT_SURVEYS } from "@/survey/constants";
import { getBaseURL } from ".";
import { getStatusOfEncounterAssociatedWithCuraiVisit } from "./generated";
import {
  getGetAccessEligibilityStatusQueryKey,
  getGetClinicStatusForPatientQueryKey,
  getGetPatientResponsibilityStatusQueryKey,
  getGetUserProfileQueryKey,
} from "./queries";
import {
  invalidateConversationHistory,
  setConversationUnread,
} from "./queries/conversation";

export const SOCKET_RECONNECT_DELAY = 5000;

const useSocketPrimitive = () => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const getAccessToken = useGetAuthToken();
  useEffect(() => {
    if (!socket) {
      setSocket(
        io(getBaseURL(), {
          autoConnect: false,
          withCredentials: true,
          auth(cb) {
            getAccessToken().then((token) => {
              cb({ token });
            });
          },
        }),
      );
    }
  }, [socket, getAccessToken]);
  return socket;
};

type SocketEvent = {
  event_type: "message";
  data: EventModel;
};
interface SocketError extends Error {
  data: {
    code: string;
  };
}

export const useSocket = () => {
  const socket = useSocketPrimitive();
  const queryClient = useQueryClient();
  const router = usePropagatedRouter();
  const { setModalContent } = useContext(LayoutContext);
  const { setVideoCallConversationId } = useVideoStore();
  const {
    userId,
    isSignedIn,
    isInitializing: isAuthInitializing,
  } = useAuthState();
  const { markUserDateOfBirthRequested } = useAgeVerificationStore();
  const { setConversationNudge } = useNudgeStore();
  const { setCanJoinVideoCall } = useVideoStore();

  const onDisconnect = useCallback(() => {
    setTimeout(() => {
      socket?.connect();
    }, SOCKET_RECONNECT_DELAY);
  }, [socket]);

  const onMessage = useCallback(
    (event: SocketEvent) => {
      if (!event?.data) {
        captureException(
          "Socket message event received with missing data payload",
          { extra: { event_type: event?.event_type } },
        );
        return;
      }
      logger.info(logger.fmt`Message received of type ${event.event_type}`, {
        category: "socket",
        event,
      });
      const conversationId = event.data.conversation_id;
      try {
        if (event.event_type === "message") {
          setConversationUnread(conversationId, queryClient);
          const queryKey = infiniteMessagesQueryKey(conversationId);
          queryClient.setQueryData(
            queryKey,
            updateInfiniteMessagesFromSocket(event.data),
          );
        }
      } catch {
        captureException("Error parsing socket event data", {
          extra: { data: event.data },
        });
      }
    },
    [queryClient],
  );

  const onSideEffect = useCallback(
    async (event: FrontendSideEffectEvent) => {
      logger.info(
        logger.fmt`Side effect received of type ${event.side_effect_type}`,
        {
          category: "socket",
          event,
        },
      );
      switch (event.side_effect_type) {
        case "visit_taken": {
          queryClient.invalidateQueries({
            predicate: (query: Query<any, any, any, any>) =>
              query.queryKey[0].startsWith("/clinic/visit") &&
              query.queryKey[0].endsWith("/status"),
          });
          queryClient.invalidateQueries({
            queryKey: getGetClinicStatusForPatientQueryKey(),
            exact: false,
          });

          // do not route if this is a video visit
          let visitType: EncounterStartType | null | undefined;
          try {
            const visit = await getStatusOfEncounterAssociatedWithCuraiVisit(
              event.taken_conversation_id,
            );
            visitType = visit.type;
          } catch (error) {
            captureException(error, {
              data: {
                reason: "Failed to resolve visit type on visit_taken",
                conversationId: event.taken_conversation_id,
              },
            });
          }

          if (visitType === EncounterStartType.AUDIOVISUAL) {
            setVideoCallConversationId(event.taken_conversation_id);
          } else {
            router.navigate(`/conversation/${event.taken_conversation_id}`);
            setModalContent(null);
          }
          break;
        }
        case "conversation_closed":
          const conversationId = event.conversation_id;
          queryClient.invalidateQueries({
            queryKey: ["conversation", "infinite", conversationId],
            exact: false,
          });
          queryClient.invalidateQueries({
            predicate: (query: Query<any, any, any, any>) =>
              query.queryKey[0].startsWith("/clinic/visit") &&
              query.queryKey[0].endsWith("/status"),
          });
          queryClient.refetchQueries({
            queryKey: getGetClinicStatusForPatientQueryKey(),
            exact: false,
          });
          setCanJoinVideoCall(false);
          break;
        case "csat_requested": {
          const surveyConfig = CSAT_SURVEYS[event.survey_key];
          if (userId && event.conversation_id && surveyConfig) {
            useCSATSurveyStore.getState().showSurvey({
              surveyKey: event.survey_key,
              questions: surveyConfig.questions,
              conversationId: event.conversation_id,
              encounterId: event.encounter_id,
            });
          }
          break;
        }
        case "conversation_list_updated":
          invalidateConversationHistory(queryClient);
          queryClient.invalidateQueries({ queryKey: ["conversationGroup"] });
          break;
        case "signup_nudge":
          if (event.nudge_type === "outreach") {
            setConversationNudge(event.conversation_id, event.nudge_type);
          }
          break;
        case "video_visit_started":
          setCanJoinVideoCall(true);
          break;
        case "date_of_birth_request":
          markUserDateOfBirthRequested();
          break;
        case "image_generation_updated":
          updateImageGenerationStatus(
            queryClient,
            event.conversation_id,
            event.event_id,
            event.images,
          );
          break;
        case "id_verification_updated":
          queryClient.invalidateQueries({
            queryKey: getGetUserProfileQueryKey(),
            exact: false,
          });
          break;
        case "patient_responsibility_updated":
          queryClient.invalidateQueries({
            queryKey: getGetPatientResponsibilityStatusQueryKey(),
            exact: false,
          });
          break;
        case "access_eligibility_updated":
          // The event names no enrollment and carries no result, so refetch every check.
          queryClient.invalidateQueries({
            queryKey: getGetAccessEligibilityStatusQueryKey(),
            exact: false,
          });
          break;
        default:
          break;
      }
    },
    [
      queryClient,
      router,
      setModalContent,
      markUserDateOfBirthRequested,
      setConversationNudge,
      setVideoCallConversationId,
      userId,
      setCanJoinVideoCall,
    ],
  );

  const onConnectError = useCallback(
    (error: Error) => {
      // Cast to SocketError to access the data property
      const socketError = error as SocketError;
      if (socket?.active) {
        logger.error(
          "Ephemeral socket connection error, will attempt to reconnect naturally via Socket.IO.",
          {
            category: "socket",
            error: socketError,
          },
        );
      } else {
        logger.error(
          "Socket connection denied by server, will attempt to reconnect via a manual re-try.",
          {
            category: "socket",
            error: socketError,
          },
        );
        setTimeout(() => {
          socket?.connect();
        }, SOCKET_RECONNECT_DELAY);
      }
    },
    [socket],
  );

  useEffect(() => {
    // Keeping isSigned in the dependency array to disconnect on authState change
    // undefined state is when Clerk is loading
    // we need to wait for isAuthInitializing so anonymous user is created (dependency of so much in the app)
    if (isSignedIn === undefined || isAuthInitializing) {
      return;
    }
    socket?.connect();

    return () => {
      if (socket?.connected) {
        socket?.disconnect();
      }
    };
  }, [isSignedIn, isAuthInitializing, socket]);

  useEffect(() => {
    if (!socket) {
      return;
    }
    socket.on("disconnect", onDisconnect);
    socket.on("message", onMessage);
    socket.on("side_effect", onSideEffect);
    socket.on("connect_error", onConnectError);

    return () => {
      if (socket) {
        socket.removeAllListeners("connect");
        socket.removeAllListeners("disconnect");
        socket.removeAllListeners("message");
        socket.removeAllListeners("side_effect");
        socket.removeAllListeners("connect_error");
      }
    };
  }, [socket, onDisconnect, onMessage, onSideEffect, onConnectError]);

  return socket;
};
